import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/utils/cn';

// ============================================
// TYPES
// ============================================

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}

// ============================================
// COMPONENT
// ============================================

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', error, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          'flex h-10 w-full rounded-md border bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)]',
          'placeholder:text-[var(--text-tertiary)]',
          'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent',
          'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-[var(--bg-secondary)]',
          error ? 'border-red-500 focus:ring-red-500' : 'border-[var(--border-primary)]',
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

Input.displayName = 'Input';

// ============================================
// INPUT WITH LABEL
// ============================================

interface InputWithLabelProps extends InputProps {
  label: string;
  id: string;
  helperText?: string;
  errorMessage?: string;
}

export function InputWithLabel({
  label,
  id,
  helperText,
  errorMessage,
  error,
  className,
  ...props
}: InputWithLabelProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={id} className="block text-sm font-medium text-[var(--text-secondary)]">
        {label}
      </label>
      <Input id={id} error={error ?? !!errorMessage} {...props} />
      {helperText && !errorMessage && (
        <p className="text-sm text-[var(--text-tertiary)]">{helperText}</p>
      )}
      {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
    </div>
  );
}
